import { Injectable, signal, OnDestroy } from '@angular/core';
import { ModalService } from '@app/modal/modal.service';
import { environment } from '@env/environment';
import { Item } from '@models/item.interface';
import { KingHistory } from '@models/king-history';
import { KingHit } from '@models/king-hit.interface';
import { KingUser } from '@models/king-user.interface';
import { King } from '@models/king.interface';
import { SessionService } from '@shared/session.service';
import { SocketService } from '@shared/socket.service';
import { sleep } from '@utils';

@Injectable({
  providedIn: 'root',
})
export class KingService implements OnDestroy {
  king = signal<King | null>(null);
  timer = signal<number>(30);
  maxTimer = 250;
  queue = signal<KingUser[]>([]);
  history = signal<KingHistory[]>([]);
  gameStarted = signal<boolean>(false);
  hitSignal = signal<KingHit>(null);
  private timerInterval: any;
  cdnBase = environment.cdnBase;

  constructor(private socketService: SocketService, private sessionService: SessionService, private modalService: ModalService) {
    this.initKingService();
    this.startTimer();
  }

  private startTimer() {
    this.timerInterval = setInterval(() => {
      if (this.timer() > 0 && this.king()?.status === 'part_waiting') {
        this.timer.update((t) => t - 1);
      }
    }, 1000);
  }

  initKingService() {
    this.socketService.socket.on('king_queue', (data: { queue: KingUser[] }) => {
      this.queue.set(data.queue);
    });

    this.socketService.socket.on('king_queue_add', (data: KingUser) => {
      this.queue.update((q) => [...q, data]);
    });

    this.socketService.socket.on('king_queue_remove', (data: number) => {
      this.queue.update((q) => q.filter((user) => user?.id !== data));
    });

    this.socketService.socket.on('king_update', (data) => {
      const kingFixed = this.fixUser(data.king);

      if (kingFixed) {
        this.king.update((k) => ({ ...k, king: this.fixUser(data.king) }));

        if (this.sessionService.profile()?.steamid === kingFixed.steamid) {
          this.modalService.close();
        }
      }
    });

    this.socketService.socket.on('part_update', (data) => {
      const partFixed = this.fixUser(data.part);
      if (partFixed) {
        this.king.update((k) => ({ ...k, part: this.fixUser(data.part) }));

        if (this.sessionService.profile()?.steamid === partFixed.steamid) {
          this.modalService.close();
        }
      }
    });

    this.socketService.socket.on('king_initiate', (data: any) => {
      console.log('🚀 ~ KingService ~ this.socketService.socket.on ~ king_initiate:', data);
      this.king.set(data);

      this.timer.set(data.time - data.timenow);
      if (this.timer() < 0) this.timer.set(0);
      if (data.time === -1) this.timer.set(-1);

      this.king.update((k) => ({
        ...k,
        king: this.fixUser(k.king),
        part: this.fixUser(k.part),
      }));
    });

    this.socketService.socket.on('show_king_history', (data: any) => {
      const sortedHistory = data.history.sort((a: King, b: King) => {
        if (a.gameid === b.gameid) {
          return b.roundid - a.roundid;
        }
        return b.gameid - a.gameid;
      });

      this.history.set(
        sortedHistory.map((game: King) => ({
          ...game,
          king: this.fixUser(game.king),
          part: this.fixUser(game.part),
        })),
      );
    });

    this.socketService.socket.on('king_attack', async (hit: KingHit) => {
      console.log('🚀 ~ KingService ~ this.socketService.socket.on ~ hit:', hit);

      this.gameStarted.set(true);

      hit.toUser = hit.by === 'king' ? this.king().part : this.king().king;

      hit.damage = (hit.damage / 100) * (hit.toUser?.initial_hp ?? 0);

      this.hitSignal.set(hit);
      await sleep(2000);

      const hpAfter = hit.toUser.initial_hp * (hit.hp_now / 100);

      if (hit.by === 'king') {
        this.king.update((k) => {
          k.part.hp = hpAfter;
          return k;
        });
      } else {
        this.king.update((k) => {
          k.king.hp = hpAfter;
          return k;
        });
      }

      const currentKing = this.king();
      if (currentKing.part.hp <= 0 || currentKing.king.hp <= 0) {
        await sleep(4100);
        this.gameStarted.set(false);
        this.hitSignal.set(null);
        // A slow ack here has nothing to tell the user, and must not reject unhandled.
        this.socketService.emit('show_king_history', undefined, { noErrorMessage: true }).catch((): void => undefined);
      }
    });

    this.preloadImages();
  }

  ngOnDestroy() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  private preloadImages() {
    const images = [
      `${this.cdnBase}/king/ak-hitmarkers.png`,
      `${this.cdnBase}/king/pistol-hitmarkers.png`,
      `${this.cdnBase}/king/rock-hitmarkers.png`,
      `${this.cdnBase}/king/sniper-hitmarkers.png`,
    ];
    for (const image of images) {
      const img = new Image();
      img.src = image;
    }
  }

  async joinKing(items: Item[], type: number) {
    return await this.socketService.emit('king_deposit', { skins: items, type });
  }

  /**
   * Request the current game state from the server.
   * Called when entering the King page (and on reconnect) to ensure we have the latest state.
   */
  requestGameState(): void {
    this.socketService.socket.emit('king_join');
  }

  private fixUser(user: KingUser) {
    if (user?.steamid?.toString() === '-1' || !user) {
      return null;
    }

    const value = user.value === 0 && Array.isArray(user.skins) ? user.skins.reduce((sum, skin) => sum + (skin.price ?? 0), 0) : user.value;

    const initialHP = value * 1000;

    return {
      ...user,
      value,
      hp: initialHP * ((user.hp ?? 100) / 100),
      initial_hp: initialHP,
    };
  }
}
